import styled from "@emotion/styled";
import React, { ReactElement } from "react";
import { sortClockedVisitsByTime } from "../../clockInOutUtils";
import { isLoading, Loading } from "../../Loading";
import { VisitInstanceDetails } from "../../messages";
import { STRINGS } from "../../strings";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { translate } from "../../translate";
import { List } from "../../utils/List";
import { AppBottomSheet } from "../Generic/AppBottomSheet";
import { AppButton } from "../Generic/AppButton";
import { AppCard, AppCardContent } from "../Generic/AppCard";
import { AppOverlay } from "../Generic/AppOverlay";
import Flex from "../Generic/Flex";
export interface Props {
  caregiver: AppCaregiver | Loading;
  isOpen: boolean;
  presentVisits: List<VisitInstanceDetails>;
  onClickStartNow: (visit: VisitInstanceDetails) => void;
  onRequestClose: () => void;
}
export function MultiVisitsReminderBottomSheet(props: Props): ReactElement {
  if (isLoading(props.caregiver)) {
    return <></>;
  }

  return (
    <AppOverlay>
      <MultiVisitsReminderBottomSheet.Root
        isOpen={props.isOpen}
        rounded={true}
        onRequestClose={props.onRequestClose}
      >
        <Flex direction="row" alignItems="start" justifyContent="space-between">
          <MultiVisitsReminderBottomSheet.Title>
            {`${translate(STRINGS.HI)} ${props.caregiver.firstName}!`}
          </MultiVisitsReminderBottomSheet.Title>
        </Flex>
        <MultiVisitsReminderBottomSheet.Subtitle>
          {`${translate(STRINGS.YOU_HAVE_MORE_THAN_ONE_SHIFT_THAT_IS_ACTIVE_NOW)}`}
        </MultiVisitsReminderBottomSheet.Subtitle>
        <MultiVisitsReminderBottomSheet.QuestionSubtitle>
          {`${translate(STRINGS.WHAT_WOULD_YOU_LIKE_TO_DO_FIRST)}`}
        </MultiVisitsReminderBottomSheet.QuestionSubtitle>
        <MultiVisitsReminderBottomSheet.Content>
          {sortClockedVisitsByTime(props.presentVisits.toArray()).map((visit) => (
            <AppCard key={visit.id.toString()}>
              <AppCardContent>
                <Flex direction="row" justifyContent="space-between" alignItems="end">
                  <MultiVisitsReminderBottomSheet.Column direction="column">
                    <MultiVisitsReminderBottomSheet.VisitData>{`${translate(STRINGS.VISIT_WITH)} ${
                      visit.confidentialVisitDetails.patientName
                    }`}</MultiVisitsReminderBottomSheet.VisitData>
                    <MultiVisitsReminderBottomSheet.VisitData>{`${visit.startTimeLocal.toLocalTime()} - ${visit.endTimeLocal.toLocalTime()}`}</MultiVisitsReminderBottomSheet.VisitData>
                  </MultiVisitsReminderBottomSheet.Column>
                  <MultiVisitsReminderBottomSheet.VisitButton
                    variant="filled"
                    color="primary"
                    onClick={() => props.onClickStartNow(visit)}
                  >
                    {visit.clockInTime === null
                      ? translate(STRINGS.START_SHIFT)
                      : translate(STRINGS.FINISH_SHIFT)}
                  </MultiVisitsReminderBottomSheet.VisitButton>
                </Flex>
              </AppCardContent>
            </AppCard>
          ))}
        </MultiVisitsReminderBottomSheet.Content>

        <AppButton variant="link" color="black" onClick={props.onRequestClose}>
          {translate(STRINGS.I_WILL_DO_IT_LATER)}
        </AppButton>
      </MultiVisitsReminderBottomSheet.Root>
    </AppOverlay>
  );
}

MultiVisitsReminderBottomSheet.Root = styled(AppBottomSheet)`
  .AppBottomSheet_Container {
    border-radius: 24px 24px 0 0;
    color: #283847;
    padding: 8px;

    .clockRangesContainer {
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 12px;
      display: flex;
      flex-direction: column;
      gap: 8px;

      .clockRange {
        display: flex;
        gap: 5px;
      }
    }

    .AppButton {
      margin-top: 4px;
      width: 100%;
    }
    overflow: visible;
    max-height: 420px !important;
  }
`;

MultiVisitsReminderBottomSheet.Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-bottom: 20px;
`;

MultiVisitsReminderBottomSheet.Title = styled.span`
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  margin-bottom: 20px;
`;

MultiVisitsReminderBottomSheet.Subtitle = styled.div`
  font-weight: 300;
  font-size: 18px;
  line-height: 24px;
  margin-bottom: 20px;
`;

MultiVisitsReminderBottomSheet.QuestionSubtitle = styled(MultiVisitsReminderBottomSheet.Subtitle)`
  font-weight: 500;
`;

MultiVisitsReminderBottomSheet.VisitData = styled.span`
  font-weight: 300;
  font-size: 14px;
  line-height: 24px;
`;

MultiVisitsReminderBottomSheet.VisitButton = styled(AppButton)`
  width: 90px !important;
  height: 30px !important;
  white-space: nowrap;
  font-size: 12px !important;
  margin: 0 !important;
  bottom: 0 !important;
`;

MultiVisitsReminderBottomSheet.Column = styled(Flex)`
  margin-bottom: 8px;
`;
